/**
 * L'état de la Création de personnage.
 *
 * La feuille est la seule source de vérité ; tout le reste — soldes, plafonds,
 * aperçu de la fiche, anomalies — est **dérivé** par le moteur à chaque
 * changement. L'interface ne calcule rien et ne mémorise rien de calculé :
 * c'est la même promesse que le domaine, tenue jusqu'à l'écran.
 */

import {
  d100 as lancerD100,
  etat as etatDuMoteur,
  feuilleVide,
  referentiel as chargerReferentiel,
  regionTiree,
  tirerCapacite,
  type Etat,
  type Feuille,
  type Referentiel,
} from '../rules'

/** Les étapes de l'écran, dans l'ordre du livre. */
export const ETAPES = [
  { id: 'region', nom: "Région d'origine" },
  { id: 'classe', nom: 'Classe' },
  { id: 'caracteristiques', nom: 'Caractéristiques' },
  { id: 'competences', nom: 'Compétences' },
  { id: 'maitrises', nom: 'Maîtrise' },
  { id: 'equipement', nom: 'Équipement' },
  { id: 'histoire', nom: 'Identité' },
] as const

export type EtapeId = (typeof ETAPES)[number]['id']

const CLE = 'lotc.creation'

function relire(): Feuille {
  try {
    const brut = localStorage.getItem(CLE)
    if (brut) return { ...feuilleVide(), ...JSON.parse(brut) }
  } catch {
    // Un stockage illisible ne doit pas empêcher de créer un personnage :
    // on repart d'une feuille vide plutôt que de refuser de s'afficher.
  }
  return feuilleVide()
}

export function creerAtelier() {
  const referentiel: Referentiel = chargerReferentiel()
  let feuille = $state<Feuille>(relire())
  let etape = $state<EtapeId>('region')
  /** Le dernier tirage de Capacité, pour que l'écran puisse le commenter. */
  let dernierTirage = $state<{ verdict: string; capacite: string | null; d100: number } | null>(
    null,
  )

  const etat = $derived<Etat>(etatDuMoteur(feuille))

  $effect(() => {
    localStorage.setItem(CLE, JSON.stringify(feuille))
  })

  return {
    referentiel,
    get feuille() {
      return feuille
    },
    get etat() {
      return etat
    },
    get etape() {
      return etape
    },
    set etape(valeur: EtapeId) {
      etape = valeur
    },
    get dernierTirage() {
      return dernierTirage
    },

    /** Les anomalies qui se corrigent à une étape donnée. */
    anomaliesDe(id: EtapeId) {
      return etat.anomalies.filter((a) => a.etape === id)
    },

    nommer(nom: string) {
      feuille.nom = nom
    },

    /**
     * Changer de Région efface les Capacités qu'elle avait offertes : elles ne
     * sont plus proposées, et les garder ferait un personnage impossible.
     * Un tirage, lui, reste acquis — il ne dépend pas de la Région.
     */
    choisirRegion(id: string) {
      feuille.region = id
      feuille.capacites = feuille.capacites.filter((c) => c.d100 != null)
    },

    tirerRegion() {
      const de = lancerD100()
      const id = regionTiree(de)
      if (id) this.choisirRegion(id)
      return de
    },

    /**
     * Changer de Classe efface ce qui en dépendait : les Compétences avancées
     * de l'ancienne, et les arbitrages rendus sur ses dotations.
     */
    choisirClasse(id: string) {
      const avancees = new Set(
        referentiel.competences.filter((c) => c.avancee).map((c) => c.id),
      )
      for (const cle of Object.keys(feuille.competences)) {
        if (avancees.has(cle)) delete feuille.competences[cle]
      }
      feuille.dotations = []
      feuille.classe = id
    },

    basculerCapacite(id: string) {
      const i = feuille.capacites.findIndex((c) => c.capacite === id && c.d100 == null)
      if (i >= 0) feuille.capacites.splice(i, 1)
      else feuille.capacites.push({ capacite: id, d100: null })
    },

    /**
     * Lance le dé et applique le verdict du domaine.
     *
     * La relance est ici, pas dans le moteur : le livre dit « relancez », le
     * domaine dit « il faut relancer », et c'est l'écran qui tient le dé.
     */
    tirerUneCapacite() {
      for (let essai = 0; essai < 50; essai += 1) {
        const de = lancerD100()
        const verdict = tirerCapacite(feuille, de)
        dernierTirage = verdict
        if (verdict.verdict === 'retenue' && verdict.capacite) {
          feuille.capacites.push({ capacite: verdict.capacite, d100: de })
          return verdict
        }
        if (verdict.verdict === 'hors_table') return verdict
      }
      return dernierTirage
    },

    retirerTirage() {
      feuille.capacites = feuille.capacites.filter((c) => c.d100 == null)
      dernierTirage = null
    },

    /** Reprendre le dé : l'ancienne tirée s'efface avant le nouveau lancer. */
    relancerTirage() {
      this.retirerTirage()
      return this.tirerUneCapacite()
    },

    fixerCarac(id: string, valeur: number) {
      feuille.caracteristiques[id] = valeur
    },

    fixerCompetence(id: string, valeur: number) {
      const depart = etat.competences_octroyees[id] ?? 0
      if (valeur <= depart && !(id in etat.competences_octroyees)) {
        delete feuille.competences[id]
      } else {
        feuille.competences[id] = valeur
      }
    },

    basculerMaitrise(id: string) {
      const i = feuille.maitrises.indexOf(id)
      if (i >= 0) feuille.maitrises.splice(i, 1)
      else feuille.maitrises.push(id)
    },

    trancher(noeud: string, index: number, choix: Partial<Feuille['dotations'][number]>) {
      const autres = feuille.dotations.filter(
        (d) => !(d.noeud === noeud && d.index === index),
      )
      feuille.dotations = [...autres, { noeud, index, ...choix }]
    },

    /** Repartir de zéro, sans laisser de trace de l'ancienne feuille. */
    recommencer() {
      feuille = feuilleVide()
      dernierTirage = null
      etape = 'region'
    },
  }
}

export type Atelier = ReturnType<typeof creerAtelier>
